import { forwardRef, useState } from 'react';
import React from 'react';

import { fromPlacement, type Placement } from '@/components/dropdownPlacement';
import { Menu, MenuProps } from '@/components/Menu';
import { Button, type ButtonProps } from '@/components/ui/button';
import {
  DropdownMenu as RadixDropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { DropdownOption } from '@/types/dropdown.types';
import { cn } from '@/utils/classname';

export interface DropdownMenu extends MenuProps {
  placement?: Placement;
  onClose?: () => void;
  width?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * If true, the menu will match the width of the trigger button.
   */
  fullWidthMenu?: boolean;
  /**
   * Called when "Select All" is clicked in multi-select mode.
   */
  onSelectAll?: () => void;
  /**
   * Called when "Deselect All" is clicked in multi-select mode.
   */
  onDeselectAll?: () => void;
}

export interface DropdownProps extends Omit<ButtonProps, 'onSelect'> {
  menu?: DropdownMenu;
  focusType?: 'click' | 'focus';
  asChild?: boolean;
  /**
   * If true, the menu will match the width of the trigger button.
   */
  fullWidthMenu?: boolean;
  /**
   * @deprecated No-op. Radix Content self-portals; inline rendering is no longer
   * supported. Retained for API compatibility — passing this has no effect.
   */
  disablePortal?: boolean;

  /**
   * If true, the menu does NOT move focus into its content when it opens.
   * Use this for search-as-you-type menus where an external input (a sibling
   * of the trigger, not the trigger itself) must keep focus so the user can
   * keep typing while the results list filters. Defaults to Radix's normal
   * behavior (focus moves into the content on open).
   */
  keepFocusOnOpen?: boolean;

  // Button trigger props
  label?: React.ReactNode;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  isLoading?: boolean;
  styleType?: string;

  // ========================================
  // Legacy Compatibility Props (Deprecated)
  // ========================================
  // These props provide backward compatibility with chariot-ui Dropdown
  // and will be removed in a future version. Please migrate to the new API.

  /** @deprecated Use menu.items instead. Pass array of items with onClick callbacks. */
  options?: DropdownOption[];

  /** @deprecated Use label instead */
  placeholder?: string;

  /** @deprecated Use startIcon instead */
  icon?: React.ReactNode;

  /** @deprecated Use menu.onClick instead. Called when an option is selected. */
  onSelect?: (option: DropdownOption) => void;

  /** @deprecated Border is always visible, use custom styling instead */
  border?: boolean;

  /** @deprecated Use menu.className instead */
  listClassName?: string;

  /** @deprecated Use fullWidthMenu instead */
  fitWidth?: boolean;

  /** @deprecated Use label instead. Tooltip/title for the dropdown button. */
  title?: string;
}

export const Dropdown = forwardRef<HTMLButtonElement, DropdownProps>(
  function Dropdown(props: DropdownProps, ref) {
    const {
      menu: menuProp,
      asChild,
      focusType, // eslint-disable-line @typescript-eslint/no-unused-vars -- deprecated: Radix opens on click; retained for API compatibility
      fullWidthMenu: propFullWidthMenu,
      disablePortal = false, // eslint-disable-line @typescript-eslint/no-unused-vars -- deprecated no-op: Radix Content self-portals
      keepFocusOnOpen = false,
      size: buttonSize,
      label,
      startIcon: propStartIcon,
      endIcon: propEndIcon,
      isLoading,
      styleType,
      // Legacy props
      options,
      placeholder,
      icon,
      onSelect,
      border, // eslint-disable-line @typescript-eslint/no-unused-vars
      listClassName,
      fitWidth,
      title,
      className: propClassName,
      ...buttonProps
    } = props;

    // Transform legacy props to new API
    const menu: DropdownMenu = React.useMemo(() => {
      // If using new API, use it directly
      if (menuProp) {
        return {
          ...menuProp,
          className: listClassName || menuProp.className,
        } as DropdownMenu;
      }

      // If using legacy API, transform to new API
      if (options) {
        return {
          items: options.map(opt => {
            // Handle dividers
            if (opt.isDivider) {
              return {
                label: '',
                value: 'divider',
                isDivider: true,
              };
            }

            return {
              label: opt.label,
              value: opt.value ?? opt.label,
              icon: opt.icon,
              disabled: opt.disabled,
              count: opt.count,
              onClick: () => {
                // Call the option's onClick if provided
                opt.onClick?.();
                // Call onSelect with the option if provided
                onSelect?.(opt);
              },
            };
          }),
          className: listClassName,
          placement: 'bottom-start' as Placement,
        } as DropdownMenu;
      }

      // Fallback - empty menu
      return { items: [] } as DropdownMenu;
    }, [menuProp, options, onSelect, listClassName]);

    const {
      placement = 'bottom-start',
      onClose,
      fullWidthMenu: menuFullWidthMenu,
      ...menuProps
    } = menu;

    // Standard controlled/uncontrolled pattern.
    const [internalOpen, setInternalOpen] = useState(false);
    const open = menu.open !== undefined ? menu.open : internalOpen;
    const setOpen = (value: boolean) => {
      setInternalOpen(value);
      menu.onOpenChange?.(value);
    };

    // Determine if fullWidthMenu is enabled (from either prop or menu)
    // Legacy fitWidth prop maps to fullWidthMenu
    const fullWidthMenu = propFullWidthMenu ?? menuFullWidthMenu ?? fitWidth;

    // Use label priority: label > placeholder > title
    const buttonLabel = label || placeholder || title;

    // Determine if single-select mode and get selected item with severity
    const selectedItem = React.useMemo(() => {
      if (menuProps.multiSelect) return null;

      const selectedValue = Array.isArray(menuProps.value)
        ? menuProps.value[0]
        : menuProps.value;
      if (!selectedValue) return null;

      return menuProps.items?.find(item => item.value === selectedValue);
    }, [menuProps.multiSelect, menuProps.value, menuProps.items]);

    // Use icon priority: startIcon from buttonProps > legacy icon prop > severity indicator
    const buttonStartIcon =
      propStartIcon ||
      icon ||
      (selectedItem?.severity && (
        <div
          className={`size-2.5 rounded-full border bg-chariot-status-${selectedItem.severity} border-chariot-stroke-${selectedItem.severity}`}
        />
      ));

    // Map legacy styleType to shadcn variant
    function mapStyleTypeToVariant(styleType?: string): ButtonProps['variant'] {
      switch (styleType) {
        case 'primary':
          return 'default';
        case 'secondary':
          return 'secondary';
        case 'text':
          return 'ghost';
        case 'textPrimary':
          return 'link';
        case 'header':
          return 'secondary';
        case 'error':
          return 'destructive';
        case 'rounded':
          return 'outline';
        case 'icon':
          return 'ghost';
        case 'none':
          return 'ghost';
        case 'tertiary':
          return 'ghost';
        default:
          return 'ghost'; // Dropdown triggers are typically ghost-styled
      }
    }

    const { side, align } = fromPlacement(placement);

    const menuBody = (
      <Menu
        {...menuProps}
        onClick={updatedValue => {
          menuProps?.onClick?.(updatedValue);
        }}
      />
    );

    return (
      <RadixDropdownMenu
        open={open}
        onOpenChange={(o: boolean) => {
          setOpen(o);
          if (!o) {
            onClose?.();
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          {asChild ? (
            (props.children as React.ReactElement)
          ) : (
            <Button
              ref={ref}
              size={buttonSize}
              variant={mapStyleTypeToVariant(styleType)}
              className={cn(
                styleType === 'rounded' && 'rounded-full',
                propClassName
              )}
              disabled={isLoading || undefined}
              {...buttonProps}
            >
              {buttonStartIcon}
              {buttonLabel || props.children}
              {propEndIcon}
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          data-testid="dropdown-menu"
          side={side}
          align={align}
          sideOffset={10}
          collisionPadding={10}
          // Radix's onOpenAutoFocus (inherited from FocusScope) is not surfaced
          // by ComponentPropsWithoutRef on the shadcn wrapper, so it's spread in
          // here. preventDefault keeps focus where it is on open — required for
          // search-as-you-type menus whose input is a sibling of the trigger.
          {...(keepFocusOnOpen
            ? {
                onOpenAutoFocus: (event: Event) => event.preventDefault(),
              }
            : {})}
          style={
            fullWidthMenu
              ? { width: 'var(--radix-dropdown-menu-trigger-width)' }
              : { width: menuProps.width }
          }
          className="scrollbar-dark max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-auto p-0"
        >
          {menuBody}
        </DropdownMenuContent>
      </RadixDropdownMenu>
    );
  }
);
